import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGalleryImages, addImage, deleteImage, GalleryImage } from '../../../hooks/useGalleryDB';

interface UploadItem {
  file: File;
  preview: string;
  alt: string;
  status: 'pending' | 'uploading' | 'done' | 'error';
}

export default function GalleryAdmin() {
  const navigate = useNavigate();
  const { images, loading, refresh } = useGalleryImages();
  const [uploadQueue, setUploadQueue] = useState<UploadItem[]>([]);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error'>('success');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const isAuthenticated = sessionStorage.getItem('galleryAdmin');
    if (!isAuthenticated) {
      navigate('/gallery/admin/login');
    }
  }, [navigate]);

  const showMessage = (text: string, type: 'success' | 'error' = 'success') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => setMessage(''), 4000);
  };

  const processFiles = (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const validFiles = fileArray.filter((f) => {
      if (!f.type.startsWith('image/')) return false;
      if (f.size > 20 * 1024 * 1024) return false;
      return true;
    });

    if (validFiles.length !== fileArray.length) {
      showMessage(`${fileArray.length - validFiles.length} file(s) skipped (must be images under 20MB)`, 'error');
    }

    const newItems: UploadItem[] = validFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      alt: file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' '),
      status: 'pending',
    }));

    setUploadQueue((prev) => [...prev, ...newItems]);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processFiles(e.target.files);
      e.target.value = '';
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) {
      processFiles(e.dataTransfer.files);
    }
  };

  const updateAlt = (index: number, value: string) => {
    setUploadQueue((prev) =>
      prev.map((item, i) => (i === index ? { ...item, alt: value } : item))
    );
  };

  const removeFromQueue = (index: number) => {
    setUploadQueue((prev) => {
      const item = prev[index];
      URL.revokeObjectURL(item.preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const uploadAll = async () => {
    const pendingIndexes = uploadQueue
      .map((item, i) => (item.status === 'pending' ? i : -1))
      .filter((i) => i !== -1);

    if (pendingIndexes.length === 0) {
      showMessage('No images to upload', 'error');
      return;
    }

    let successCount = 0;

    for (const i of pendingIndexes) {
      setUploadQueue((prev) =>
        prev.map((q, idx) => (idx === i ? { ...q, status: 'uploading' } : q))
      );

      try {
        const base64 = await fileToBase64(uploadQueue[i].file);
        const newImage: GalleryImage = {
          id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          src: base64,
          alt: uploadQueue[i].alt || uploadQueue[i].file.name,
          uploadedAt: Date.now(),
        };

        await addImage(newImage);
        successCount++;

        setUploadQueue((prev) =>
          prev.map((q, idx) => (idx === i ? { ...q, status: 'done' } : q))
        );
      } catch {
        setUploadQueue((prev) =>
          prev.map((q, idx) => (idx === i ? { ...q, status: 'error' } : q))
        );
      }
    }

    await refresh();
    showMessage(`${successCount} photo${successCount !== 1 ? 's' : ''} uploaded to gallery!`);

    setTimeout(() => {
      setUploadQueue((prev) => {
        prev.filter((q) => q.status === 'done').forEach((q) => URL.revokeObjectURL(q.preview));
        return prev.filter((q) => q.status !== 'done');
      });
    }, 1500);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this image?')) {
      await deleteImage(id);
      await refresh();
      showMessage('Image deleted');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('galleryAdmin');
    navigate('/gallery/admin/login');
  };

  const pendingCount = uploadQueue.filter((q) => q.status === 'pending').length;

  return (
    <div className="min-h-screen bg-[#0A1628]">
      {/* Header */}
      <div className="bg-[#0F1F3A] border-b border-gray-800 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <h1 className="text-xl font-bold text-white">Gallery Admin</h1>
          <div className="flex items-center gap-3">
            <a
              href="/gallery"
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors whitespace-nowrap text-sm cursor-pointer"
            >
              View Gallery
            </a>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors whitespace-nowrap text-sm cursor-pointer"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-2 ${messageType === 'success' ? 'bg-green-900/30 text-green-400 border border-green-800' : 'bg-red-900/30 text-red-400 border border-red-800'}`}>
            <i className={messageType === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'}></i>
            {message}
          </div>
        )}

        {/* Upload Zone */}
        <div className="bg-[#0F1F3A] rounded-xl p-6 mb-8 border border-gray-800">
          <h2 className="text-lg font-bold text-white mb-4">Upload Photos</h2>

          <div
            className={`border-2 border-dashed rounded-xl p-10 text-center transition-all cursor-pointer ${isDragging ? 'border-[#FFD700] bg-[#FFD700]/5' : 'border-gray-700 hover:border-[#FFD700]/50 hover:bg-[#FFD700]/5'}`}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <i className="ri-image-add-line text-5xl text-[#FFD700]"></i>
            </div>
            <p className="text-white font-semibold text-lg mb-1">Tap to select photos</p>
            <p className="text-gray-400 text-sm">Select multiple photos at once — works on mobile</p>
            <p className="text-gray-500 text-xs mt-2">JPG, PNG, HEIC up to 20MB each</p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              capture={undefined}
              className="hidden"
              onChange={handleFileInput}
            />
          </div>

          {/* Queue Preview */}
          {uploadQueue.length > 0 && (
            <div className="mt-6">
              <div className="flex items-center justify-between mb-3">
                <p className="text-gray-300 text-sm font-medium">
                  {uploadQueue.length} photo{uploadQueue.length !== 1 ? 's' : ''} selected
                </p>
                <button
                  onClick={() => {
                    uploadQueue.forEach((q) => URL.revokeObjectURL(q.preview));
                    setUploadQueue([]);
                  }}
                  className="text-gray-500 hover:text-red-400 text-xs transition-colors cursor-pointer"
                >
                  Clear all
                </button>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-5">
                {uploadQueue.map((item, index) => (
                  <div key={index} className="relative bg-[#0A1628] rounded-lg overflow-hidden border border-gray-700">
                    {item.status === 'uploading' && (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-10">
                        <i className="ri-loader-4-line text-[#FFD700] text-3xl animate-spin"></i>
                      </div>
                    )}
                    {item.status === 'done' && (
                      <div className="absolute inset-0 bg-green-900/60 flex items-center justify-center z-10">
                        <i className="ri-checkbox-circle-fill text-green-400 text-3xl"></i>
                      </div>
                    )}
                    {item.status === 'error' && (
                      <div className="absolute inset-0 bg-red-900/60 flex items-center justify-center z-10">
                        <i className="ri-close-circle-fill text-red-400 text-3xl"></i>
                      </div>
                    )}
                    {item.status === 'pending' && (
                      <button
                        onClick={(e) => { e.stopPropagation(); removeFromQueue(index); }}
                        className="absolute top-2 right-2 z-20 w-6 h-6 flex items-center justify-center bg-black/70 rounded-full text-white hover:bg-red-600 transition-colors cursor-pointer"
                      >
                        <i className="ri-close-line text-xs"></i>
                      </button>
                    )}
                    <div className="w-full h-32">
                      <img src={item.preview} alt="" className="w-full h-full object-cover" />
                    </div>
                    <div className="p-2">
                      <input
                        type="text"
                        value={item.alt}
                        onChange={(e) => updateAlt(index, e.target.value)}
                        placeholder="Caption..."
                        className="w-full px-2 py-1 bg-[#0F1F3A] border border-gray-700 rounded text-white text-xs placeholder-gray-500 focus:outline-none focus:border-[#FFD700]"
                        disabled={item.status !== 'pending'}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={uploadAll}
                disabled={pendingCount === 0}
                className="w-full py-4 bg-[#FFD700] hover:bg-[#FFC700] text-[#0A1628] font-bold rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer text-lg"
              >
                {pendingCount > 0
                  ? `⬆ Upload ${pendingCount} Photo${pendingCount !== 1 ? 's' : ''} to Gallery`
                  : 'All Uploaded ✓'}
              </button>
            </div>
          )}
        </div>

        {/* Existing Gallery */}
        <div className="bg-[#0F1F3A] rounded-xl p-6 border border-gray-800">
          <h2 className="text-lg font-bold text-white mb-4">
            Uploaded Photos ({loading ? '...' : images.length})
          </h2>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <i className="ri-loader-4-line text-[#FFD700] text-3xl animate-spin"></i>
            </div>
          ) : images.length === 0 ? (
            <p className="text-gray-400 text-center py-12">No photos uploaded yet — use the upload area above</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {images.map((image) => (
                <div key={image.id} className="group relative bg-[#0A1628] rounded-lg overflow-hidden border border-gray-700">
                  <div className="w-full h-40">
                    <img
                      src={image.src}
                      alt={image.alt}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-3">
                    <p className="text-gray-400 text-xs mb-2 line-clamp-1">{image.alt}</p>
                    <button
                      onClick={() => handleDelete(image.id)}
                      className="w-full py-1.5 bg-red-600/80 hover:bg-red-600 text-white text-xs rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-delete-bin-line mr-1"></i>Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
